// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet } from 'react-native';

import { GAME_COL_WIDTH, PLAYER_COL_WIDTH, TOTAL_COL_WIDTH } from './utils';
import appColors, { alpha } from '@/theme/colors';
import { panelTextShadow } from '@/theme/textShadow';
import appFonts from '@/theme/typography';

export const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 14,
    gap: 8,
    paddingBottom: 40,
    backgroundColor: appColors.background.app,
  },
  backLink: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  backLinkText: {
    color: appColors.accent.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  headerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: appColors.background.panel,
    padding: 10,
  },
  headerImage: {
    width: 100,
    height: 100,
    borderRadius: 12,
  },
  headerImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 12,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
  },
  headerText: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  background: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  scroll: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
  },
  banner: {
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    borderWidth: 2,
    borderColor: alpha(appColors.accent.glow, 0.42),
    padding: 12,
  },
  bannerTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: appColors.text.primary,
    letterSpacing: 0.6,
    fontFamily: appFonts.brand,
  },
  bannerMeta: {
    color: appColors.text.primary,
    marginTop: 2,
    fontFamily: appFonts.brand,
  },
  title: {
    fontSize: 20,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  primaryButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: alpha(appColors.surface.tint, 0.95),
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
  },
  primaryText: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 12,
  },
  meta: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  error: {
    color: appColors.status.errorText,
    fontFamily: appFonts.brand,
  },
  waitingCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.secondary, 0.62),
    backgroundColor: alpha(appColors.surface.tintMid, 0.86),
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 8,
  },
  waitingTitle: {
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '800',
    fontFamily: appFonts.brand,
  },
  waitingText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontFamily: appFonts.brand,
  },
  roundPickerCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: appColors.background.panel,
    padding: 10,
    gap: 8,
  },
  roundPickerTitle: {
    fontSize: 11,
    fontFamily: appFonts.brand,
    color: appColors.text.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...panelTextShadow(0.55, 2, 3),
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: alpha(appColors.surface.tint, 0.95),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
    flex: 1,
    fontFamily: appFonts.brand,
  },
  roundPickerHint: {
    fontSize: 12,
    color: appColors.accent.primary,
    fontWeight: '700',
    marginLeft: 12,
    fontFamily: appFonts.brand,
  },
  tableCard: {
    borderRadius: 12,
    borderWidth: 2,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: appColors.background.panel,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingTop: 8,
    backgroundColor: 'transparent',
  },
  resultsButtonRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    backgroundColor: 'transparent',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginHorizontal: 10,
    marginBottom: 10,
  },
  tableHeaderTitle: {
    fontSize: 12,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  resultsButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: alpha(appColors.surface.tint, 0.95),
  },
  resultsButtonText: {
    fontSize: 12,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    textDecorationLine: 'none',
  },
  resultsActionButton: {
    minWidth: 0,
    width: 106,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.interactive,
    paddingHorizontal: 8,
  },
  resultsActionButtonText: {
    fontSize: 11,
    lineHeight: 13,
    fontWeight: '700',
  },
  roundSectionsCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: appColors.background.panel,
    padding: 10,
    gap: 10,
  },
  roundSectionsTitle: {
    fontSize: 11,
    fontFamily: appFonts.brand,
    color: appColors.text.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...panelTextShadow(0.55, 2, 3),
  },
  roundSectionCard: {
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.36),
    borderRadius: 10,
    backgroundColor: alpha(appColors.background.panel, 0.75),
    padding: 8,
    gap: 8,
  },
  roundSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  roundSectionTitle: {
    fontSize: 11,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    flex: 1,
    ...panelTextShadow(0.55, 2, 3),
  },
  roundSectionMeta: {
    fontSize: 10,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  roundSectionTable: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.28),
  },
  roundSectionTableHeader: {
    flexDirection: 'row',
    backgroundColor: alpha(appColors.surface.tint, 0.88),
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  roundSectionHeaderText: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 11,
    textAlign: 'right',
    flex: 1,
  },
  roundSectionPlayerHeader: {
    textAlign: 'left',
  },
  roundSectionTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  roundSectionTableRowEven: {
    backgroundColor: appColors.background.panelAlt,
  },
  roundSectionTableRowOdd: {
    backgroundColor: alpha(appColors.background.panelAlt, 0.82),
  },
  roundSectionPlayerCell: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 13,
    flex: 1,
    textAlign: 'left',
  },
  roundSectionPointsCell: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 13,
    width: 72,
    textAlign: 'right',
  },
  roundSectionEmpty: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 11,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  membersCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: appColors.background.panel,
    padding: 10,
    gap: 8,
  },
  membersTitle: {
    fontSize: 11,
    fontFamily: appFonts.brand,
    color: appColors.text.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...panelTextShadow(0.55, 2, 3),
  },
  membersHint: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 11,
  },
  membersList: {
    gap: 6,
    backgroundColor: 'transparent',
  },
  memberRow: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.28),
    paddingVertical: 8,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  memberRowEven: {
    backgroundColor: appColors.background.panelAlt,
  },
  memberRowOdd: {
    backgroundColor: alpha(appColors.background.panelAlt, 0.82),
  },
  memberMain: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  memberName: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 14,
  },
  memberMeta: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 11,
  },
  membersEmpty: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    fontSize: 11,
  },
  table: {
    paddingVertical: 6,
    marginLeft: 10,
    marginBottom: 12,
  },
  tableHeaderTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: alpha(appColors.surface.tint, 0.88),
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  tableHeaderBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: alpha(appColors.surface.tint, 0.88),
  },
  roundHeaderOffset: {
    width: PLAYER_COL_WIDTH + TOTAL_COL_WIDTH,
    height: '100%',
    backgroundColor: alpha(appColors.surface.tint, 0.88),
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: appColors.background.panelAlt,
  },
  rowEven: {
    backgroundColor: appColors.background.panelAlt,
  },
  rowOdd: {
    backgroundColor: alpha(appColors.background.panelAlt, 0.82),
  },
  cell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.32),
    fontSize: 11,
    textAlign: 'center',
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
    backgroundColor: 'transparent',
  },
  playerCell: {
    width: PLAYER_COL_WIDTH,
    textAlign: 'left',
    fontWeight: '600',
    fontSize: 14,
    fontFamily: appFonts.brand,
  },
  gameCell: {
    width: GAME_COL_WIDTH,
    fontSize: 10,
    textTransform: 'uppercase',
    fontFamily: appFonts.brand,
  },
  totalCell: {
    width: TOTAL_COL_WIDTH,
    fontWeight: '900',
    fontSize: 13,
    color: appColors.text.primary,
    ...panelTextShadow(0.35, 1, 2),
    fontFamily: appFonts.brand,
  },
  totalCellHeader: {
    backgroundColor: alpha(appColors.surface.tint, 0.9),
  },
  playerHeaderCell: {
    backgroundColor: alpha(appColors.surface.tint, 0.9),
  },
  gameHeaderCell: {
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.32),
    paddingVertical: 6,
    paddingHorizontal: 4,
    backgroundColor: alpha(appColors.surface.tint, 0.88),
  },
  roundGroupHeaderCell: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameHeaderText: {
    fontSize: 11,
    textAlign: 'center',
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  roundHeaderCell: {
    backgroundColor: alpha(appColors.surface.tint, 0.7),
  },
  roundNumberText: {
    fontSize: 10,
    color: appColors.text.primary,
    fontWeight: '600',
    fontFamily: appFonts.brand,
  },
  roundCell: {
    width: GAME_COL_WIDTH,
  },
  claimCell: {
    width: 86,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  claimButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.42),
    backgroundColor: alpha(appColors.surface.tint, 0.95),
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  claimButtonDisabled: {
    opacity: 0.6,
  },
  claimButtonText: {
    fontSize: 11,
    color: appColors.text.primary,
    fontWeight: '700',
    fontFamily: appFonts.brand,
  },
  gameRoundsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resultCell: {
    backgroundColor: 'transparent',
  },
  resultText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontFamily: appFonts.brand,
  },
  roundsCardTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 6,
    fontFamily: appFonts.brand,
  },
  roundRowLabel: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  roundsSummary: {
    marginTop: 4,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  summaryList: {
    marginTop: 4,
  },
  summaryItem: {
    fontSize: 12,
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  graphWrap: {
    paddingVertical: 6,
    gap: 12,
  },
  graphLegend: {
    gap: 6,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendLabel: {
    color: appColors.text.primary,
    fontSize: 12,
    fontFamily: appFonts.brand,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalCard: {
    width: '92%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 16,
    backgroundColor: appColors.background.panel,
    gap: 12,
  },
  modalTableCard: {
    maxWidth: 700,
  },
  roundPickerModalCard: {
    maxWidth: 520,
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  modalError: {
    color: appColors.status.dangerText,
    fontFamily: appFonts.brand,
  },
  modalButtonRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'flex-end',
    width: '100%',
    flexWrap: 'wrap',
  },
  modalActionButton: {
    width: 108,
    minWidth: 108,
    height: 34,
  },
  modalActionText: {
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 13,
  },
  modalSecondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: appColors.border.strong,
    minWidth: 96,
    alignItems: 'center',
  },
  modalSecondaryText: {
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  modalCloseButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: appColors.background.interactive,
    minWidth: 96,
    alignItems: 'center',
  },
  modalCloseText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontFamily: appFonts.brand,
  },
  roundPickerOptions: {
    gap: 8,
    backgroundColor: 'transparent',
  },
  roundPickerOption: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.28),
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: appColors.background.panelAlt,
  },
  roundPickerOptionTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: appFonts.brand,
  },
  roundPickerOptionSubtitle: {
    color: appColors.text.primary,
    fontSize: 12,
    marginTop: 2,
    fontFamily: appFonts.brand,
  },
  resultsTableRoundHeaderRow: {
    flexDirection: 'row',
    backgroundColor: alpha(appColors.surface.tint, 0.88),
  },
  resultsTableRoundHeaderCell: {
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.32),
    alignItems: 'center',
  },
  resultsTableRoundHeaderText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontFamily: appFonts.brand,
  },
  resultsTableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: alpha(appColors.surface.tint, 0.9),
  },
  resultsTableRow: {
    flexDirection: 'row',
    backgroundColor: appColors.background.panelAlt,
  },
  resultsTableCell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.32),
    fontSize: 14,
    color: appColors.text.primary,
    textAlign: 'center',
    minWidth: 40,
    fontFamily: appFonts.brand,
  },
  resultsTablePlayerCell: {
    width: 120,
    textAlign: 'left',
  },
  resultsTableHoleCell: {
    minWidth: 40,
  },
  // Summary-style presentation (mirrors the round summary page design language).
  summarySectionCard: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: appColors.border.accent,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
    marginBottom: 16,
  },
  summarySectionTitle: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '800',
    fontFamily: appFonts.brand,
  },
  summaryHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: alpha(appColors.accent.glow, 0.3),
    paddingBottom: 6,
    backgroundColor: 'transparent',
  },
  summaryHeaderText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 13,
    fontFamily: appFonts.brand,
  },
  summaryHeaderPlayer: {
    flex: 1,
  },
  summaryHeaderValue: {
    minWidth: 56,
    textAlign: 'right',
  },
  summaryPlayerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.26),
    backgroundColor: appColors.background.panelAlt,
    gap: 8,
  },
  summaryLeaderRow: {
    backgroundColor: alpha(appColors.accent.glow, 0.18),
    borderColor: appColors.border.accent,
  },
  summaryRank: {
    fontSize: 16,
    minWidth: 28,
    textAlign: 'center',
    color: appColors.text.primary,
    fontFamily: appFonts.brand,
  },
  summaryNameBlock: {
    flex: 1,
    gap: 2,
    backgroundColor: 'transparent',
  },
  summaryPlayerName: {
    color: appColors.text.secondary,
    fontSize: 15,
    fontFamily: appFonts.brand,
  },
  summaryPlayerMeta: {
    color: alpha(appColors.text.secondary, 0.7),
    fontSize: 12,
    fontFamily: appFonts.brand,
  },
  summaryMerits: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '900',
    fontFamily: appFonts.brand,
    minWidth: 56,
    textAlign: 'right',
  },
  summaryStatRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 4,
    backgroundColor: 'transparent',
  },
  summaryStatLabel: {
    color: alpha(appColors.text.secondary, 0.85),
    fontSize: 13,
    fontFamily: appFonts.brand,
  },
  summaryStatValue: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: appFonts.brand,
    flexShrink: 1,
    textAlign: 'right',
  },
  summaryGameGroup: {
    gap: 8,
  },
  summaryGameGroupHeader: {
    fontSize: 13,
    fontWeight: '800',
    color: appColors.text.secondary,
    fontFamily: appFonts.brand,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 4,
  },
  summaryGameCard: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.26),
    padding: 10,
    gap: 6,
  },
  summaryGameHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  summaryGameIcon: {
    width: 30,
    height: 30,
    borderRadius: 6,
  },
  summaryGameTitle: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '800',
    fontFamily: appFonts.brand,
  },
  summaryGameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
    backgroundColor: 'transparent',
  },
  summaryGameRowPlayer: {
    color: appColors.text.primary,
    fontSize: 14,
    flex: 1,
    fontFamily: appFonts.brand,
  },
  summaryGameRowPoints: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    fontFamily: appFonts.brand,
  },
  roundSectionWinnerRow: {
    backgroundColor: alpha(appColors.accent.glow, 0.22),
  },
});
